import { type FormEvent, useEffect, useState } from 'react';
import { Search } from 'lucide-react';
import { toast } from 'sonner';

import { AppShell } from '@/components/layout/AppShell';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { AuthApiError, getAuthClient } from '@/api/client';
import type { JoinRequest, OrganizationSearchItem } from '@/api/types';
import { useDebounce } from '@/hooks/useDebounce';
import { errorMessage } from '@/lib/errorMap';
import { useAuthStore } from '@/stores/useAuthStore';

const ORG_TYPE_LABEL: Record<string, string> = {
  onto_internal: '온토 내부',
  customer: '고객사',
  subcontractor: '협력사',
  other: '기타',
};

export function FindOrgPage() {
  const refreshMe = useAuthStore((s) => s.refreshMe);
  const [q, setQ] = useState('');
  const debouncedQ = useDebounce(q, 300);
  const [results, setResults] = useState<OrganizationSearchItem[]>([]);
  const [searching, setSearching] = useState(false);
  const [target, setTarget] = useState<OrganizationSearchItem | null>(null);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [pending, setPending] = useState<JoinRequest[]>([]);

  // 무소속 사용자의 pending 요청 표시
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await getAuthClient().myJoinRequests();
        if (!cancelled) setPending(list.filter((r) => r.status === 'pending'));
      } catch {
        // 무시 — 무소속 사용자에 권한 부재 등 케이스
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // debounced 검색
  useEffect(() => {
    if (debouncedQ.trim().length < 3) {
      setResults([]);
      return;
    }
    let cancelled = false;
    setSearching(true);
    getAuthClient()
      .searchOrganizations(debouncedQ.trim())
      .then((items) => {
        if (!cancelled) setResults(items);
      })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof AuthApiError && e.code === 'rate_limited') {
          toast.error('검색이 너무 잦습니다. 잠시 후 다시 시도하세요');
        }
      })
      .finally(() => {
        if (!cancelled) setSearching(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedQ]);

  const onRequest = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!target) return;
    setSubmitting(true);
    try {
      const jr = await getAuthClient().requestJoin(target.id, message);
      toast.success('가입 요청이 제출됐습니다');
      setPending((prev) => [jr, ...prev]);
      setTarget(null);
      setMessage('');
      void refreshMe();
    } catch (err) {
      if (err instanceof AuthApiError) toast.error(errorMessage(err.code));
      else toast.error('네트워크 오류');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppShell>
      <div className="mx-auto flex max-w-2xl flex-col gap-6">
        <div>
          <h1 className="text-2xl font-semibold">조직 찾기</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            계정은 생성됐지만 아직 소속 조직이 없습니다. 조직을 검색해 가입 요청하세요.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Search className="size-4" />
              조직 검색 (3자 이상)
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="조직명 또는 사업자번호의 일부"
            />
            {searching && <p className="text-xs text-muted-foreground">검색 중...</p>}
            {!searching && debouncedQ.length >= 3 && results.length === 0 && (
              <p className="text-xs text-muted-foreground">결과 없음</p>
            )}
            <ul className="flex flex-col divide-y">
              {results.map((o) => (
                <li
                  key={o.id}
                  className="flex items-center justify-between py-2"
                >
                  <div>
                    <div className="font-medium">{o.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {ORG_TYPE_LABEL[o.org_type] ?? o.org_type}
                    </div>
                  </div>
                  <Button size="sm" onClick={() => setTarget(o)}>
                    가입 요청
                  </Button>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {pending.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">대기 중인 요청</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="flex flex-col divide-y">
                {pending.map((r) => (
                  <li key={r.id} className="flex flex-col gap-1 py-2 text-sm">
                    <div className="flex flex-wrap items-baseline gap-2">
                      <span className="font-medium text-foreground">
                        {r.organization_name ?? '(이름 미상)'}
                      </span>
                      <span className="font-mono text-xs text-muted-foreground break-all">
                        {r.organization_id}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {r.requested_role} · {new Date(r.requested_at).toLocaleString('ko-KR')}
                    </div>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}
      </div>

      <Dialog open={target !== null} onOpenChange={(o) => !o && setTarget(null)}>
        <DialogContent>
          <form onSubmit={onRequest} className="flex flex-col gap-4">
            <DialogHeader>
              <DialogTitle>{target?.name} 가입 요청</DialogTitle>
              <DialogDescription>
                자기소개 또는 소속 증빙을 적어주세요. 해당 조직 관리자가 검토합니다.
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-2">
              <Label htmlFor="msg">메시지</Label>
              <Textarea
                id="msg"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                placeholder="예) 서초현장 안전관리자 홍길동입니다."
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setTarget(null)}
              >
                취소
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? '제출 중...' : '요청 보내기'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
